import { useState } from 'react';

import * as Dialog from '@radix-ui/react-dialog';
import { X, Download } from 'lucide-react';
import { utils, writeFile } from 'xlsx';

import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/use-toast';
import { formatDateTime } from '@/utils/format';

import type { CallWithContact } from '@repo/shared-types';

interface ExportCallsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  calls: CallWithContact[];
}

interface ColumnOption {
  key: string;
  label: string;
  getValue: (call: CallWithContact) => string | number | null | undefined;
}

const COLUMN_OPTIONS: ColumnOption[] = [
  {
    key: 'id',
    label: 'Call ID',
    getValue: (call) => call.id,
  },
  {
    key: 'contact_name',
    label: 'Contact Name',
    getValue: (call) => call.contact?.name || call.contact_id || '-',
  },
  {
    key: 'phone_number',
    label: 'Phone Number',
    getValue: (call) => call.phone_number || call.contact?.phone || '-',
  },
  {
    key: 'campaign_name',
    label: 'Campaign',
    getValue: (call) => call.campaign?.name || '-',
  },
  {
    key: 'configuration_name',
    label: 'Configuration',
    getValue: (call) => call.configuration?.name || '-',
  },
  {
    key: 'direction',
    label: 'Direction',
    getValue: (call) => call.direction,
  },
  {
    key: 'status',
    label: 'Status',
    getValue: (call) => call.status,
  },
  {
    key: 'outcome',
    label: 'Outcome',
    getValue: (call) => call.outcome || '-',
  },
  {
    key: 'duration',
    label: 'Duration (seconds)',
    getValue: (call) => call.duration,
  },
  {
    key: 'attempt_number',
    label: 'Attempt Number',
    getValue: (call) => call.attempt_number || '-',
  },
  {
    key: 'executed_at',
    label: 'Executed At',
    getValue: (call) => formatDateTime(call.executed_at),
  },
  {
    key: 'availability_data',
    label: 'Availability Data',
    getValue: (call) => (call.availability_data ? JSON.stringify(call.availability_data) : '-'),
  },
  {
    key: 'lead_data',
    label: 'Lead Data',
    getValue: (call) => (call.lead_data ? JSON.stringify(call.lead_data) : '-'),
  },
  {
    key: 'callback_data',
    label: 'Callback Data',
    getValue: (call) => (call.callback_data ? JSON.stringify(call.callback_data) : '-'),
  },
  {
    key: 'recording_path',
    label: 'Recording Path',
    getValue: (call) => call.recording_path || '-',
  },
];

const DEFAULT_SELECTED_COLUMNS = [
  'id',
  'contact_name',
  'phone_number',
  'campaign_name',
  'direction',
  'status',
  'outcome',
  'duration',
  'executed_at',
];

export const ExportCallsModal = ({ open, onOpenChange, calls }: ExportCallsModalProps) => {
  const [selectedColumns, setSelectedColumns] = useState<string[]>(DEFAULT_SELECTED_COLUMNS);
  const [isExporting, setIsExporting] = useState(false);

  const handleToggleColumn = (columnKey: string) => {
    setSelectedColumns((prev) =>
      prev.includes(columnKey) ? prev.filter((key) => key !== columnKey) : [...prev, columnKey],
    );
  };

  const handleSelectAll = () => {
    setSelectedColumns(COLUMN_OPTIONS.map((col) => col.key));
  };

  const handleDeselectAll = () => {
    setSelectedColumns([]);
  };

  const handleExport = () => {
    if (selectedColumns.length === 0) {
      toast({
        variant: 'destructive',
        title: 'No Columns Selected',
        description: 'Please select at least one column to export',
      });
      return;
    }

    setIsExporting(true);

    try {
      // Filter columns based on selection
      const columnsToExport = COLUMN_OPTIONS.filter((col) => selectedColumns.includes(col.key));

      // Prepare data
      const data = calls.map((call) => {
        const row: Record<string, unknown> = {};
        columnsToExport.forEach((col) => {
          row[col.label] = col.getValue(call);
        });
        return row;
      });

      // Create workbook and worksheet
      const worksheet = utils.json_to_sheet(data);
      const workbook = utils.book_new();
      utils.book_append_sheet(workbook, worksheet, 'Calls');

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').split('T')[0];
      const filename = `calls_export_${timestamp}.xlsx`;

      // Save file
      writeFile(workbook, filename);

      // Close modal after successful export
      onOpenChange(false);
    } catch (error) {
      console.error('Error exporting calls:', error);
      toast({
        variant: 'destructive',
        title: 'Export Failed',
        description: 'Failed to export calls. Please try again.',
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/60" />
        <Dialog.Content className="bg-background fixed top-1/2 left-1/2 z-50 w-full max-w-2xl -translate-x-1/2 -translate-y-1/2 rounded-lg p-6 shadow-lg">
          <div className="mb-4 flex items-center justify-between">
            <Dialog.Title className="text-lg font-semibold">Export Calls to Excel</Dialog.Title>
            <Dialog.Close asChild>
              <Button variant="ghost" size="icon">
                <X className="h-4 w-4" />
              </Button>
            </Dialog.Close>
          </div>

          <div className="mb-4">
            <p className="text-muted-foreground text-sm">
              Select the columns you want to include in the export. {calls.length} call(s) will be
              exported.
            </p>
          </div>

          <div className="mb-4 flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={handleSelectAll}>
              Select All
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={handleDeselectAll}>
              Deselect All
            </Button>
          </div>

          <div className="mb-6 max-h-[50vh] overflow-y-auto rounded-md border p-4">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {COLUMN_OPTIONS.map((column) => (
                <label
                  key={column.key}
                  className="hover:bg-muted/50 flex cursor-pointer items-center space-x-2 rounded p-2"
                >
                  <input
                    type="checkbox"
                    checked={selectedColumns.includes(column.key)}
                    onChange={() => handleToggleColumn(column.key)}
                    className="border-input text-primary focus:ring-primary h-4 w-4 rounded focus:ring-2"
                  />
                  <span className="text-sm font-medium">{column.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <p className="text-muted-foreground text-sm">
              {selectedColumns.length} column(s) selected
            </p>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleExport}
                disabled={isExporting || selectedColumns.length === 0}
              >
                <Download className="h-4 w-4" />
                {isExporting ? 'Exporting...' : 'Export to Excel'}
              </Button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
